import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { OrgRole } from '@allemnii-pulse/shared';
import { api } from '../../lib/api.js';
import { consumePrimedMe } from '../../lib/mePrefetch.js';
import { useAuth } from '../auth/AuthProvider.js';

export interface MyOrg {
  id: string;
  name: string;
  role: OrgRole;
}

interface OrgContextValue {
  orgs: MyOrg[];
  loading: boolean;
  currentOrgId: string | null;
  currentOrg: MyOrg | null;
  setCurrentOrgId: (id: string) => void;
  refresh: () => Promise<void>;
}

const OrgContext = createContext<OrgContextValue | null>(null);
const STORAGE_KEY = 'pulse:currentOrgId';

/**
 * Loads the signed-in user's org memberships from GET /api/me (see docs/PLAN.md §9) and tracks
 * which one is "current" for the rest of the app — every project/task read is scoped to it.
 * There is deliberately no server concept of a "current org"; it's pure client UI state.
 */
export function OrgProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [orgs, setOrgs] = useState<MyOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentOrgId, setCurrentOrgIdState] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : localStorage.getItem(STORAGE_KEY),
  );

  async function refresh() {
    setLoading(true);
    try {
      // POST /auth/session already returned the orgs on this sign-in — reuse them instead of
      // firing a second, redundant GET /me.
      const me = consumePrimedMe() ?? (await api.get<{ orgs: MyOrg[] }>('/me'));
      setOrgs(me.orgs ?? []);
      setCurrentOrgIdState((prev) => {
        if (prev && me.orgs?.some((o) => o.id === prev)) return prev;
        return me.orgs?.[0]?.id ?? null;
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (status === 'signed-in') void refresh();
    // Intentionally re-runs only when auth status changes, not on every `refresh` identity change.
  }, [status]);

  function setCurrentOrgId(id: string) {
    setCurrentOrgIdState(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // ignore — falls back to defaulting to orgs[0] next load
    }
  }

  const currentOrg = useMemo(
    () => orgs.find((o) => o.id === currentOrgId) ?? null,
    [orgs, currentOrgId],
  );

  return (
    <OrgContext.Provider
      value={{ orgs, loading, currentOrgId, currentOrg, setCurrentOrgId, refresh }}
    >
      {children}
    </OrgContext.Provider>
  );
}

export function useOrgs(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrgs must be used within OrgProvider');
  return ctx;
}
